import React, {useEffect, useState} from "react"
import {connect} from "react-redux"

import PropTypes from "prop-types"

import {isEmpty} from "lodash"
import {TitleSection} from "~/components/"

import ShelfApi from "~/utils/ShelfApi"
import {
  Form,
  FormSection,
  NumericField,
  BooleanField,
  SelectField,
  LoadingIndicator,
  Field
} from "~/components/"
import {updateSettings} from "~/actions/settingsActions"
import {useTranslation} from "react-i18next"

const SettingsView = props => {
  const {t} = useTranslation()
  const [settings, setSettings] = useState(null)
  const [loading, setLoading] = useState(true)

  const [errors, setErrors] = useState({})

  useEffect(() => {
    getData()
  }, [])

  const stockOutStrategyOptions = [
    {id: "fifo", name: t("settings.stockOutStrategyOptions.fifo")},
    {id: "fefo", name: t("settings.stockOutStrategyOptions.fifo")}
  ]

  const articleFieldsOrderingOptions = [
    {
      name: t("settings.articleFieldsOrderingOptions.expiryDate"),
      id: ["expiry_date", "barcode", "lot_number"]
    },
    {
      name: t("settings.articleFieldsOrderingOptions.barcode"),
      id: ["barcode", "expiry_date", "lot_number"]
    }
  ]

  function getData() {
    ShelfApi.get("/settings/" + props.match.params.tenant)
      .then(res => {
        const setLimit = res.data.articles_per_location !== -1
        const usingReorderProducts = !isEmpty(res.data.reorder_email)
        const usingExpiryDateContacts = !isEmpty(res.data.expiry_date_contacts)

        const settings = {
          ...res.data,
          setLimit,
          usingReorderProducts,
          usingExpiryDateContacts,
          articles_per_location: setLimit ? res.data.articles_per_location : 0
        }
        setLoading(false)
        setSettings(settings)
      })
      .catch(err => {
        setLoading(false)
        console.log("Error loading settings: ", err)
      })
  }

  function onSubmit(settings) {
    setErrors({})

    if (!settings.setLimit || settings.articles_per_location < 1) {
      settings.articles_per_location = -1
    }
    delete settings.setLimit

    if (!settings.usingReorderProducts) settings.reorder_email = null
    delete settings.usingReorderProducts

    if (!settings.usingExpiryDateContacts) settings.expiry_date_contacts = null
    delete settings.usingExpiryDateContacts

    if (settings.using_viscosity) delete settings.article_fields_ordering

    return props.updateSettings(props.match.params.tenant, {settings})
  }

  function onChangeFlag(flag, value) {
    setSettings({
      ...settings,
      [flag]: value
    })
  }

  const redirectLink =
    props.match.params.tenant === props.user.subdomain
      ? "/"
      : "/tenants/" + props.match.params.tenant

  return (
    <div>
      <TitleSection>
        <h3>{t("settings.title")}</h3>
      </TitleSection>

      {loading && <LoadingIndicator />}

      {!!settings && (
        <React.Fragment>
          <Form
            onSubmit={onSubmit}
            then={redirectLink}
            submitLabel={t("save")}
            values={settings}
            globalError={errors ? errors.global : null}
          >
            <FormSection label={t("settings.registration")}>
              <NumericField
                label={t("settings.articleRegistrationDefaultQuantity")}
                type="text"
                name="article_registration_default_quantity"
              />
              <p className="hint">
                {t("settings.articleRegistrationDefaultQuantityHint")}
              </p>
              <BooleanField
                label={t("settings.fixedArticleRegistrationQuantityLabel")}
                name="fixed_article_registration_quantity"
              >
                {t("settings.fixedArticleRegistrationQuantity")}
              </BooleanField>
              <p className="hint">
                {t("settings.articleRegistrationDefaultQuantityHint")}
              </p>
            </FormSection>

            <FormSection label={t("settings.stock")}>
              <BooleanField
                label={t("settings.setLimitLabel")}
                name="setLimit"
                value={settings.setLimit}
                onChange={setLimit => onChangeFlag("setLimit", setLimit)}
              >
                {t("settings.setLimit")}
              </BooleanField>

              {settings.setLimit && (
                <NumericField
                  label={t("settings.setLimitLabel")}
                  type="text"
                  name="articles_per_location"
                />
              )}
            </FormSection>

            <FormSection label={t("settings.stockOut")}>
              <BooleanField
                label={t("settings.quickStockOut")}
                name="using_quick_stock_out"
              >
                {t("settings.usingQuickStockOut")}
              </BooleanField>

              <SelectField
                label={t("settings.stockOutStrategy")}
                type="Text"
                name="stock_out_strategy"
                options={stockOutStrategyOptions}
              />
              <BooleanField
                label={t("settings.prioritizePartiallyStockedOutArticlesLabel")}
                name="prioritize_partially_stocked_out_articles"
              >
                {t("settings.prioritizePartiallyStockedOutArticles")}
              </BooleanField>
              <BooleanField
                label={t("settings.deliveryNoteNumber")}
                name="using_delivery_note_number"
              >
                {t("settings.usingDeliveryNoteNumber")}
              </BooleanField>
              <p className="hint">
                {t("settings.usingDeliveryNoteNumberHint")}
              </p>

              <NumericField
                label={t("settings.stockOutDefaultQuantity")}
                type="text"
                name="stock_out_default_quantity"
              />
              <BooleanField
                label={t("settings.fixedStockOutQuantityLabel")}
                name="fixed_stock_out_quantity"
              >
                {t("settings.fixedStockOutQuantity")}
              </BooleanField>
              <p className="hint">{t("settings.fixedStockOutQuantityHint")}</p>
            </FormSection>

            <FormSection label={t("settings.restock")}>
              <NumericField
                label={t("settings.restockDefaultQuantity")}
                type="text"
                name="restock_default_quantity"
              />
              <BooleanField
                label={t("settings.fixedRestockQuantityLabel")}
                name="fixed_restock_quantity"
              >
                {t("settings.fixedRestockQuantity")}
              </BooleanField>
              <p className="hint">{t("settings.fixedRestockQuantityHint")}</p>
            </FormSection>

            {/* { <FormSection label="Viskosität">
              <BooleanField
                name="using_viscosity"
                value={this.state.settings.using_viscosity}
                onChange={visc => this.onChangeFlag("using_viscosity", visc)}
              >
                Viskosität verwenden
              </BooleanField>
            </FormSection>} */}

            {!settings.using_viscosity && (
              <FormSection label={t("settings.fields")}>
                <SelectField
                  label={t("settings.articleFieldsOrdering")}
                  type="Text"
                  name="article_fields_ordering"
                  options={articleFieldsOrderingOptions}
                />
                <p className="hint">
                  {t("settings.articleFieldsOrderingHint")}
                </p>
              </FormSection>
            )}

            <FormSection label={t("settings.reordering")}>
              <BooleanField
                label={t("settings.usingReorderProductsLabel")}
                name="usingReorderProducts"
                value={settings.usingReorderProducts}
                onChange={reorder =>
                  onChangeFlag("usingReorderProducts", reorder)
                }
              >
                {t("settings.usingReorderProducts")}
              </BooleanField>

              {settings.usingReorderProducts && (
                <React.Fragment>
                  <Field
                    label={t("settings.reorderEmail")}
                    type="email"
                    name="reorder_email"
                  />
                  <p className="hint">{t("settings.reorderEmailHint")}</p>

                  <NumericField
                    label={t("settings.defaultReorderQuantity")}
                    type="text"
                    name="default_reorder_quantity"
                  />
                  <p className="hint">
                    {t("settings.defaultReorderQuantityHint")}
                  </p>

                  <NumericField
                    label={t("settings.defaultTargetQuantity")}
                    type="text"
                    name="default_target_quantity"
                  />
                  <p className="hint">
                    {t("settings.defaultTargetQuantityHint")}
                  </p>

                  <NumericField
                    label={t("settings.defaultReorderUnitQuantity")}
                    type="text"
                    name="default_reorder_unit_quantity"
                  />
                  <p className="hint">
                    {t("settings.defaultReorderUnitQuantityHint")}
                  </p>
                </React.Fragment>
              )}
            </FormSection>
            <FormSection label={t("settings.expiryDate")}>
              <BooleanField
                label={t("settings.expiryDateInfoLabel")}
                name="usingExpiryDateContacts"
                value={settings.expiryDateInfo}
                onChange={toggle =>
                  onChangeFlag("usingExpiryDateContacts", toggle)
                }
              >
                {t("settings.expiryDateInfo")}
              </BooleanField>

              {settings.usingExpiryDateContacts && (
                <React.Fragment>
                  <Field
                    label={t("settings.expiryDateContacts")}
                    type="email"
                    name="expiry_date_contacts"
                  />
                </React.Fragment>
              )}
            </FormSection>

            <FormSection label={t("settings.contactPerson")}>
              <Field
                label={t("settings.name")}
                type="text"
                name="contact_name"
              />
              <Field
                label={t("settings.email")}
                type="email"
                name="contact_email"
              />
              <Field
                label={t("settings.phone")}
                type="text"
                name="contact_phone"
              />
            </FormSection>

            <FormSection label={t("settings.orderRecipient")}>
              <Field
                label={t("settings.orderRecipient")}
                type="email"
                name="order_recipient"
              />
            </FormSection>
          </Form>
        </React.Fragment>
      )}
    </div>
  )
}

SettingsView.propTypes = {
  user: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  updateSettings: PropTypes.func.isRequired
}

const mapStateToProps = ({auth}) => {
  const {user} = auth
  return {user}
}

export default connect(mapStateToProps, {updateSettings})(SettingsView)



// WEBPACK FOOTER //
// ./src/views/SettingsView.js